import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Role } from '../../../types/shared/Role';

@Injectable({
    providedIn: 'root',
})
export class AuthService {

    private currentRoleSubject = new BehaviorSubject<Role | undefined>(undefined);
    public currentRole$ = this.currentRoleSubject.asObservable();

    private baseUrl = environment.apiUrl;

    private RESSOURCE = '/auth'

    constructor(private http: HttpClient, private router: Router) {
        this.currentRoleSubject.next(this.getRole());
    }

    /**
     * Decode a JWT token.
     *
     * @param token The token to decode.
     * @return The decoded token.
     */
    private decodeToken(token: string): any {
        const parts = token.split('.');
        if (parts.length !== 3) {
            throw new Error('Invalid JWT token format.');
        }
        const payload = parts[1];
        const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));

        return JSON.parse(decodedPayload);
    }

    /**
     * Login a user.
     *
     * @param username The username of the user.
     * @param password The password of the user.
     * @return A promise that resolves with the token if the login was successful, otherwise rejects with an error.
     */
    public login(username: string, password: string): Promise<string> {
        return new Promise((resolve, reject) => {
            this.http.post(`${ this.baseUrl + this.RESSOURCE }/login`, { username, password }).subscribe(
                (response: any) => {
                    const token = response.token;
                    this.decodeToken(token);
                    this.saveToken(token);
                    this.router.navigate([ '' ]);
                    this.currentRoleSubject.next(this.getRole());
                    resolve(token);
                },
                (error) => {
                    console.error('An error occurred during login:', error);
                    reject(new Error('Invalid username or password.'));
                }
            );
        });
    }

    /**
     * Save a token to the local storage.
     *
     * @param token The token to save.
     */
    public saveToken(token: string): void {
        localStorage.setItem('token', token);
        this.currentRoleSubject.next(this.getRole());
    }

    /**
     * Get a token from the local storage.
     *
     * @return The token or null if no token is found.
     */
    public getToken(): string | null {
        return localStorage.getItem('token');
    }

    /**
     * Check if a user is logged in.
     *
     * @return true if the user is logged in, otherwise false.
     */
    public isLoggedIn(): boolean {
        return !!this.getToken();
    }

    /**
     * Logout the current user.
     */
    public async logout(): Promise<void> {
        localStorage.removeItem('token');
    }

    /**
     * Check if the current user has at least one of the given roles.
     *
     * @param roles The roles to check.
     * @returns true if the user has any of the roles, otherwise false.
     */
    public hasAnyRole(roles: Role[]): boolean {
        return roles.some((role) => this.hasRole(role));
    }

    /**
     * Check if the current user has a specific role.
     *
     * @param role The role to check.
     * @returns true if the user has the role, otherwise false.
     */
    public hasRole(role: Role): boolean {
        const token = this.getToken();
        if (!token) {
            return false;
        }

        try {
            const decoded = this.decodeToken(token);
            const roles: string[] = decoded.roles || [];

            // Extract the rights from each role and flatten the result
            const authorities = roles.reduce((acc: any, role: any) => {
                if (role && Array.isArray(role.rights)) {
                    return acc.concat(role.rights);
                }
                return acc;
            }, []);

            // If the user has 'admin' role, return 'true'
            if (authorities.includes(Role.SAMPLE_STUDIO_ADMIN)) {
                return true;
            }

            // A process owner may do everything a maintainer or producer may do.
            if (authorities.includes(Role.SAMPLE_STUDIO_PROCESS_OWNER)
                && ( role === Role.SAMPLE_STUDIO_MAINTAINER || role === Role.SAMPLE_STUDIO_PRODUCER )) {
                return true;
            }

            return authorities.includes(role);
        } catch (error) {
            console.error('Failed to decode token or check role:', error);
            return false;
        }
    }

    /**
     * Get the role of the current user.
     *
     * @return The role of the current user or undefined if the role is not found.
     */
    public getRole(): Role | undefined {
        const token = this.getToken();
        if (!token) {
            return undefined;
        }

        try {
            const decoded = this.decodeToken(token);
            const roles = decoded.roles;

            if (!roles || roles.length === 0) {
                return undefined;
            }

            // Extract the rights from each role and flatten the result
            const authorities = roles.reduce((acc: any, role: any) => {
                if (role && Array.isArray(role.rights)) {
                    return acc.concat(role.rights);
                }
                return acc;
            }, []);

            // If the user has 'admin' role, return 'admin'
            if (authorities.includes('SAMPLE_STUDIO_ADMIN')) {
                return Role.SAMPLE_STUDIO_ADMIN;
            } else if (authorities.includes('SAMPLE_STUDIO_MAINTAINER')) {
                return Role.SAMPLE_STUDIO_MAINTAINER;
            } else if (authorities.includes('SAMPLE_STUDIO_PROCESS_OWNER')) {
                return Role.SAMPLE_STUDIO_PROCESS_OWNER;
            } else if (authorities.includes('SAMPLE_STUDIO_PRODUCER')) {
                return Role.SAMPLE_STUDIO_PRODUCER;
            } else if (authorities.includes('SAMPLE_STUDIO_RETAILER')) {
                return Role.SAMPLE_STUDIO_RETAILER;
            } else if (authorities.includes('SAMPLE_STUDIO_READ_ONLY')) {
                return Role.SAMPLE_STUDIO_READ_ONLY;
            }

            return undefined
        } catch (error) {
            console.error('Failed to decode token or check role:', error);
            return undefined;
        }
    }

    /**
     * Producers must not see vehicle data. getRole() returns the highest role, so process owners
     * and admins (who also pass the producer role check) still see the vehicle models.
     *
     * @return true if the current user may see vehicle data, otherwise false.
     */
    public canSeeVehicleModels(): boolean {
        return this.getRole() !== Role.SAMPLE_STUDIO_PRODUCER;
    }

}
